if (!customElements.get("carousel-slider")) {
  customElements.define(
    "carousel-slider",
    class CarouselSlider extends HTMLElement {
      constructor() {
        super();

        this.carousel = null;
        this.flickity = null;

        this.reinitTimer = null;
        this.resizeTimer = null;

        this.handleWindowResize =
          this.handleWindowResize.bind(this);
      }

      connectedCallback() {
        this.carousel = this.querySelector(
          '[class*="grid-carousel"]'
        );

        if (!this.carousel) {
          return;
        }

        /*
         * Initial Flickity setup.
         */
        this.initialize();

        /*
         * Wait for the Records theme / Safari
         * layout to settle.
         *
         * Then completely destroy Flickity and
         * create a fresh instance.
         */
        this.reinitTimer = setTimeout(() => {
          if (!this.isConnected) {
            return;
          }

          this.destroyCarousel();
          this.initialize();

          /*
           * Final recalculation after the new
           * instance has rendered.
           */
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this.refreshCarousel();
            });
          });
        }, 150);

        /*
         * Use normal resize events instead of
         * ResizeObserver.
         */
        window.addEventListener(
          "resize",
          this.handleWindowResize
        );

        window.addEventListener(
          "orientationchange",
          this.handleWindowResize
        );
      }


      initialize() {
        if (!this.carousel) {
          return;
        }

        /*
         * Safety check.
         *
         * Destroy any Flickity instance already
         * attached to this element.
         */
        const existing =
          Flickity.data(this.carousel);

        if (existing) {
          existing.destroy();
        }

        this.flickity = null;


        /*
         * Theme settings.
         */
        const autoplayEnabled =
          this.getAttribute("data-autoplay") === "true";

        const speed = parseInt(
          this.getAttribute("data-speed"),
          10
        );

        const autoPlaySpeed =
          Number.isFinite(speed)
            ? speed * 1000
            : 5000;

        const groupCells =
          this.getAttribute(
            "data-group-cells"
          ) !== "false";

        const wrapAround =
          this.getAttribute(
            "data-wrap-around"
          ) === "true";


        /*
         * Make sure the carousel is visible before
         * Flickity measures it.
         */
        this.carousel.style.display = "block";


        /*
         * Create Flickity.
         */
        this.flickity = new Flickity(
          this.carousel,
          {
            autoPlay:
              autoplayEnabled
                ? autoPlaySpeed
                : false,

            cellAlign: "left",

            groupCells,

            contain: true,

            resize: true,

            draggable: true,

            prevNextButtons: false,

            fade: false,

            cellSelector: ".grid-item",

            initialIndex: 0,

            pageDots: false,

            wrapAround,

            accessibility: false,

            imagesLoaded: true,

            /*
             * Important for Records theme.
             */
            watchCSS: true,

            selectedAttraction: 0.015,

            friction: 0.25
          }
        );


        /*
         * Recalculate after the browser has
         * completed initial layout.
         */
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.refreshCarousel();
          });
        });


        /*
         * Fonts can alter product-card dimensions.
         */
        if (document.fonts?.ready) {
          document.fonts.ready.then(() => {
            if (!this.isConnected) {
              return;
            }

            this.refreshCarousel();
          });
        }


        /*
         * Recalculate after all page resources
         * have loaded.
         */
        if (
          document.readyState === "complete"
        ) {
          setTimeout(() => {
            if (!this.isConnected) {
              return;
            }

            this.refreshCarousel();
          }, 50);
        } else {
          window.addEventListener(
            "load",
            () => {
              if (!this.isConnected) {
                return;
              }

              this.refreshCarousel();
            },
            {
              once: true
            }
          );
        }


        /*
         * Preserve existing Records theme
         * slider-change event.
         */
        this.flickity.on(
          "change",
          (index) => {
            publish(
              PUB_SUB_EVENTS.sliderSlideChange,
              {
                id: this.getAttribute("id"),
                index
              }
            );
          }
        );


        /*
         * IMPORTANT:
         *
         * Tell carousel-navigation that a fresh
         * Flickity instance now exists.
         *
         * This fixes the arrows after the
         * 150ms destroy/reinitialize.
         */
        this.dispatchEvent(
          new CustomEvent(
            "carousel:flickity-ready"
          )
        );
      }


      /*
       * Debounced browser resize.
       */
      handleWindowResize() {
        clearTimeout(
          this.resizeTimer
        );

        this.resizeTimer = setTimeout(
          () => {
            if (!this.isConnected) {
              return;
            }

            this.refreshCarousel();
          },
          150
        );
      }


      /*
       * Recalculate the current Flickity
       * instance without rebuilding it.
       */
      refreshCarousel() {
        if (!this.flickity) {
          return;
        }

        this.flickity.reloadCells();
        this.flickity.resize();
        this.flickity.reposition();
      }


      /*
       * Completely destroy Flickity.
       */
      destroyCarousel() {
        if (!this.carousel) {
          return;
        }

        const existing =
          Flickity.data(this.carousel);

        if (existing) {
          existing.destroy();
        }

        this.flickity = null;
      }


      /*
       * Shopify section cleanup.
       */
      disconnectedCallback() {
        clearTimeout(
          this.reinitTimer
        );

        clearTimeout(
          this.resizeTimer
        );

        this.reinitTimer = null;
        this.resizeTimer = null;

        window.removeEventListener(
          "resize",
          this.handleWindowResize
        );

        window.removeEventListener(
          "orientationchange",
          this.handleWindowResize
        );

        this.destroyCarousel();

        this.carousel = null;
      }
    }
  );
}



/*
 * =========================================================
 * CAROUSEL NAVIGATION
 * =========================================================
 */

if (!customElements.get("carousel-navigation")) {
  customElements.define(
    "carousel-navigation",
    class CarouselNavigation extends HTMLElement {
      constructor() {
        super();

        this.carouselComponent = null;
        this.flickity = null;

        this.buttonPrev = null;
        this.buttonNext = null;

        this.retryTimer = null;
        this.retryCount = 0;
        this.maxRetries = 5;

        this.handlePrev =
          this.handlePrev.bind(this);

        this.handleNext =
          this.handleNext.bind(this);

        this.handleSelect =
          this.handleSelect.bind(this);

        this.handleCarouselReady =
          this.handleCarouselReady.bind(this);
      }


      connectedCallback() {
        const id =
          this.getAttribute("for");

        if (!id) {
          return;
        }

        this.wrap =
          this.dataset.wrap === "true";

        /*
         * Use getElementById rather than querySelector
         * so unusual IDs don't cause selector issues.
         */
        this.carouselComponent =
          document.getElementById(id);

        this.buttonPrev =
          this.querySelector(".js-prev");

        this.buttonNext =
          this.querySelector(".js-next");


        /*
         * Add button listeners only once.
         */
        this.buttonPrev?.addEventListener(
          "click",
          this.handlePrev
        );

        this.buttonNext?.addEventListener(
          "click",
          this.handleNext
        );


        /*
         * Listen for carousel-slider creating
         * or recreating Flickity.
         */
        this.carouselComponent?.addEventListener(
          "carousel:flickity-ready",
          this.handleCarouselReady
        );


        /*
         * Try immediately.
         */
        this.connectToFlickity();


        /*
         * Retry if carousel-slider hasn't finished
         * initializing yet.
         */
        if (!this.flickity) {
          this.scheduleRetry();
        }
      }


      /*
       * Retry connection if initialization order
       * means the slider isn't ready yet.
       */
      scheduleRetry() {
        if (
          this.retryCount >=
          this.maxRetries
        ) {
          return;
        }

        clearTimeout(
          this.retryTimer
        );

        this.retryTimer = setTimeout(
          () => {
            if (!this.isConnected) {
              return;
            }

            this.retryCount += 1;

            this.connectToFlickity();

            if (!this.flickity) {
              this.scheduleRetry();
            }
          },
          350
        );
      }


      /*
       * Fired whenever carousel-slider creates
       * a fresh Flickity instance.
       */
      handleCarouselReady() {
        this.retryCount = 0;

        this.connectToFlickity();
      }


      /*
       * Attach navigation to the CURRENT
       * Flickity instance.
       */
      connectToFlickity() {
        if (!this.carouselComponent) {
          const id =
            this.getAttribute("for");

          if (id) {
            this.carouselComponent =
              document.getElementById(id);
          }
        }

        if (!this.carouselComponent) {
          return;
        }

        const currentFlickity =
          this.carouselComponent.flickity;

        if (!currentFlickity) {
          return;
        }


        /*
         * Already connected to this exact
         * Flickity instance.
         */
        if (
          this.flickity ===
          currentFlickity
        ) {
          this.updateButtons();
          return;
        }


        /*
         * Remove listener from the old destroyed
         * Flickity instance.
         */
        if (this.flickity) {
          this.flickity.off?.(
            "select",
            this.handleSelect
          );
        }


        /*
         * Store the NEW Flickity instance.
         */
        this.flickity =
          currentFlickity;


        /*
         * Listen to selection changes on
         * the new instance.
         */
        this.flickity.on(
          "select",
          this.handleSelect
        );


        this.updateButtons();
      }


      /*
       * Previous arrow.
       *
       * Always retrieve the current Flickity
       * instance so the button never uses a
       * destroyed instance.
       */
      handlePrev(event) {
        event.preventDefault();

        const flickity =
          this.carouselComponent?.flickity;

        if (!flickity) {
          this.connectToFlickity();
          return;
        }

        flickity.previous();
      }


      /*
       * Next arrow.
       */
      handleNext(event) {
        event.preventDefault();

        const flickity =
          this.carouselComponent?.flickity;

        if (!flickity) {
          this.connectToFlickity();
          return;
        }

        flickity.next();
      }


      /*
       * Flickity slide changed.
       */
      handleSelect() {
        this.updateButtons();
      }


      /*
       * Enable / disable arrows depending
       * on carousel position.
       */
      updateButtons() {
        const flickity =
          this.carouselComponent?.flickity;

        if (!flickity) {
          return;
        }

        /*
         * Make sure our reference always points
         * to the current instance.
         */
        this.flickity = flickity;


        this.enableButton(
          this.buttonPrev
        );

        this.enableButton(
          this.buttonNext
        );


        /*
         * If wrapAround is enabled,
         * arrows should always remain enabled.
         */
        if (this.wrap) {
          return;
        }


        const selectedIndex =
          flickity.selectedIndex;

        const slides =
          flickity.slides;


        /*
         * Disable previous on first slide/group.
         */
        if (
          !slides[selectedIndex - 1]
        ) {
          this.disableButton(
            this.buttonPrev
          );
        }


        /*
         * Disable next on final slide/group.
         */
        if (
          !slides[selectedIndex + 1]
        ) {
          this.disableButton(
            this.buttonNext
          );
        }
      }


      enableButton(button) {
        if (!button) {
          return;
        }

        button.classList.remove(
          "pill-nav-item-disabled"
        );

        button.classList.remove(
          "focus-none"
        );

        button.setAttribute(
          "aria-disabled",
          "false"
        );
      }


      disableButton(button) {
        if (!button) {
          return;
        }

        button.classList.add(
          "pill-nav-item-disabled"
        );

        button.classList.add(
          "focus-none"
        );

        button.setAttribute(
          "aria-disabled",
          "true"
        );
      }


      /*
       * Shopify section cleanup.
       */
      disconnectedCallback() {
        clearTimeout(
          this.retryTimer
        );

        this.retryTimer = null;


        this.buttonPrev?.removeEventListener(
          "click",
          this.handlePrev
        );

        this.buttonNext?.removeEventListener(
          "click",
          this.handleNext
        );


        this.carouselComponent?.removeEventListener(
          "carousel:flickity-ready",
          this.handleCarouselReady
        );


        this.flickity?.off?.(
          "select",
          this.handleSelect
        );


        this.carouselComponent = null;
        this.flickity = null;

        this.buttonPrev = null;
        this.buttonNext = null;
      }
    }
  );
}